import { searchFiltersFromQuery, searchFiltersToApiQuery } from '~/utils/search-api-query'
import { MAP_CONFIG, SEARCH_FILTERS, type MapBounds, type SearchFilters, type SearchEstablishmentsResponse, type EstablishmentSearchResult, type MapPoint } from '~~/shared/search-constants'
import { formatSejoursParam } from '~~/shared/establishment-filters'
import { livingSettingQuery } from '~~/shared/living-setting'

/**
 * Vrai si le point est inclus dans la bbox (bornes inclusives).
 * Robuste pour bbox standard (ouest < est). Pas de gestion antiméridien
 * car les `mapPoints` Holicosy restent dans la zone France + DOM-TOM.
 */
function bboxContainsPoint(bbox: MapBounds, p: { lat: number, lng: number }): boolean {
  return p.lng >= bbox.west && p.lng <= bbox.east && p.lat >= bbox.south && p.lat <= bbox.north
}

/**
 * Composable pour gérer la recherche d'établissements
 * Gère l'état des filtres, la synchronisation avec l'URL et les requêtes API
 */
export function useSearch() {
  const route = useRoute()
  const router = useRouter()

  // Parser les filtres depuis l'URL (conversion pure partagée avec le compteur de l'accueil)
  function parseFiltersFromQuery(): SearchFilters {
    return searchFiltersFromQuery(route.query)
  }

  // État réactif des filtres
  const filters = ref<SearchFilters>(parseFiltersFromQuery())

  // Flag partagé : vrai pendant un loadMore ou une troncature back/forward
  // Permet aux watchers externes (page search.vue) d'ignorer les fluctuations de `page`
  const isLoadMoreInFlight = ref(false)

  // Sync URL → filtres
  // IMPORTANT : ne pas re-set filters.value si seule `page` a changé pendant un loadMore
  // (sinon le watcher de search.vue re-déclenche search() → double fetch + remplacement)
  watch(() => route.query, (newQuery, oldQuery) => {
    if (isLoadMoreInFlight.value) {
      const newKeys = Object.keys(newQuery).filter(k => k !== 'page').sort()
      const oldKeys = Object.keys(oldQuery || {}).filter(k => k !== 'page').sort()
      const onlyPageChanged = newKeys.length === oldKeys.length
        && newKeys.every(k => k === oldKeys[newKeys.indexOf(k)]
          && String(newQuery[k]) === String((oldQuery || {})[k]))
      if (onlyPageChanged) {
        // Met uniquement page à jour, préserve l'objet filtres pour éviter le re-fire du watcher deep
        filters.value = { ...filters.value, page: newQuery.page ? Number(newQuery.page) : 1 }
        return
      }
    }
    filters.value = parseFiltersFromQuery()
  }, { deep: true })

  // Convertir les filtres en query params pour l'URL
  function filtersToQuery(f: SearchFilters): Record<string, string> {
    const query: Record<string, string> = {}

    // N'écrire `type` dans l'URL que s'il restreint (gite / chambre-hotes) —
    // 'all' et absent = mode « Tous », URL laissée propre.
    if (f.type === 'gite' || f.type === 'chambre-hotes') query.type = f.type
    if (f.q) query.q = f.q
    if (f.city) query.city = f.city
    if (f.region) query.region = f.region
    if (f.checkIn) query.checkIn = f.checkIn
    if (f.checkOut) query.checkOut = f.checkOut
    if (f.department) query.department = f.department
    if (f.minAdults) query.minAdults = String(f.minAdults)
    if (f.minChildren) query.minChildren = String(f.minChildren)
    if (f.childrenAges && f.childrenAges.length > 0) query.childrenAges = f.childrenAges.join(',')
    if (f.minPrice !== undefined) query.minPrice = String(f.minPrice)
    if (f.maxPrice !== undefined) query.maxPrice = String(f.maxPrice)
    if (f.radiusKm) query.radiusKm = String(f.radiusKm)
    if (f.centerLat) query.centerLat = String(f.centerLat)
    if (f.centerLng) query.centerLng = String(f.centerLng)
    if (f.hasTableDhotes) query.hasTableDhotes = 'true'
    if (f.petsAllowed) query.petsAllowed = 'true'
    if (f.hasParking) query.hasParking = 'true'
    if (f.hasPool) query.hasPool = 'true'
    if (f.hasWifi) query.hasWifi = 'true'
    if (f.hasBikeRental) query.hasBikeRental = 'true'
    if (f.hasSauna) query.hasSauna = 'true'
    if (f.hasEVCharger) query.hasEVCharger = 'true'
    if (f.hasHorseFacilities) query.hasHorseFacilities = 'true'
    const sejours = formatSejoursParam(f.sejours)
    if (sejours) query.sejours = sejours
    Object.assign(query, livingSettingQuery(f))
    if (f.page && f.page > 1) query.page = String(f.page)
    if (f.sortBy) query.sortBy = f.sortBy
    if (f.sortOrder && f.sortOrder !== 'asc') query.sortOrder = f.sortOrder

    // Préserver locationName (label éditorial pour le bandeau de tri par distance, pas un filtre serveur).
    // Ne pas propager si plus aucun contexte géo n'est actif (sinon ghost label après reset).
    // Trim + cap 80 caractères pour éviter les URL spam venant de liens externes.
    const passthroughLocationName = route.query.locationName
    const hasGeoContext = !!f.city || !!f.q
      || (Number.isFinite(f.centerLat) && Number.isFinite(f.centerLng))
    if (
      hasGeoContext
      && typeof passthroughLocationName === 'string'
      && passthroughLocationName.trim().length > 0
    ) {
      query.locationName = passthroughLocationName.trim().slice(0, 80)
    }

    return query
  }

  // Persister les critères avancés en sessionStorage
  const { save: saveSearchCriteria } = useSearchCriteria()

  // Mettre à jour les filtres et synchroniser avec l'URL
  function updateFilters(newFilters: SearchFilters) {
    filters.value = newFilters
    router.replace({ query: filtersToQuery(newFilters) })
    if (import.meta.client) {
      saveSearchCriteria(newFilters)
    }
  }

  // Mettre à jour un filtre individuel
  function updateFilter<K extends keyof SearchFilters>(key: K, value: SearchFilters[K]) {
    const newFilters: SearchFilters = {}
    // Copy existing filters except the one being updated if value is empty
    for (const [k, v] of Object.entries(filters.value)) {
      if (k !== key && v !== undefined && v !== '' && v !== false) {
        (newFilters as Record<string, unknown>)[k] = v
      }
    }
    // Add the new value if not empty
    if (value !== undefined && value !== '' && value !== false) {
      newFilters[key] = value
    }
    // Reset page à 1 quand les filtres changent
    if (key !== 'page') {
      newFilters.page = 1
    }
    updateFilters(newFilters)
  }

  // Réinitialiser tous les filtres
  function resetFilters() {
    updateFilters({})
  }

  // Query params pour l'API : conversion partagée avec le compteur du panneau
  // « Filtres » (app/utils/search-api-query.ts).
  const queryParams = computed(() => searchFiltersToApiQuery(filters.value))

  // État des résultats
  const establishments = ref<EstablishmentSearchResult[]>([])
  const mapPoints = ref<MapPoint[]>([])
  const total = ref(0)
  const page = ref(1)
  const limit = ref(20)
  const hasMore = ref(false)
  const loading = ref(false)
  const error = ref<Error | null>(null)
  const expandedRadiusKm = ref<number | undefined>(undefined)

  // Flag pour éviter une re-recherche quand on synchronise radiusKm après auto-expansion
  let _skipNextSearch = false

  // Fonction de recherche
  async function search() {
    if (_skipNextSearch) {
      _skipNextSearch = false
      return
    }

    loading.value = true
    error.value = null
    expandedRadiusKm.value = undefined

    try {
      const response = await rawFetch<SearchEstablishmentsResponse>('/api/search/establishments', {
        query: queryParams.value,
      })

      establishments.value = response.data
      mapPoints.value = response.mapPoints
      total.value = response.meta.total
      page.value = response.meta.page
      limit.value = response.meta.limit
      hasMore.value = response.meta.hasMore
      expandedRadiusKm.value = response.meta.expandedRadiusKm

      // Synchroniser le sélecteur de rayon avec la valeur effective après auto-expansion
      if (response.meta.expandedRadiusKm !== undefined) {
        _skipNextSearch = true
        filters.value = { ...filters.value, radiusKm: response.meta.expandedRadiusKm }
        router.replace({ query: filtersToQuery(filters.value) })
      }
    }
    catch (e) {
      error.value = e as Error
    }
    finally {
      loading.value = false
    }
  }

  // Nombre maximal de pages hydratées depuis l'URL (F5/lien partagé sur ?page=N)
  const MAX_HYDRATION_PAGES = 5
  // Événement émis quand de nouveaux résultats sont accumulés (pour a11y/focus)
  const newResultsAdded = ref<{ count: number, startIndex: number, timestamp: number } | null>(null)

  // Nouvelle count de nouveaux éléments à annoncer (wrapper utilisé à la fois par loadMore et hydratation)
  function notifyNewResults(count: number, startIndex: number) {
    newResultsAdded.value = { count, startIndex, timestamp: Date.now() }
  }

  // Charger plus de résultats (accumule les résultats précédents)
  // Un seul fetch par clic : le flag isLoadMoreInFlight empêche le watcher route.query
  // de refirer `search()` après router.replace. On utilise replace (≠ push) pour que
  // le back browser sorte directement du listing, sans forcer l'utilisateur à
  // dé-cliquer chaque palier. L'état accumulé est conservé via keepalive au retour
  // d'une fiche établissement.
  async function loadMore() {
    if (!hasMore.value || loading.value) return

    isLoadMoreInFlight.value = true
    loading.value = true
    error.value = null

    try {
      const nextPage = page.value + 1
      const response = await rawFetch<SearchEstablishmentsResponse>('/api/search/establishments', {
        query: { ...queryParams.value, page: nextPage },
      })

      const previousCount = establishments.value.length
      establishments.value = [...establishments.value, ...response.data]
      total.value = response.meta.total
      page.value = response.meta.page
      limit.value = response.meta.limit
      hasMore.value = response.meta.hasMore
      expandedRadiusKm.value = response.meta.expandedRadiusKm

      filters.value = { ...filters.value, page: nextPage }
      await router.replace({ query: filtersToQuery(filters.value) })
      notifyNewResults(response.data.length, previousCount)
    }
    catch (e) {
      error.value = e as Error
    }
    finally {
      loading.value = false
      isLoadMoreInFlight.value = false
    }
  }

  // Hydratation au boot quand l'URL contient ?page=N (F5, lien partagé)
  // Fetch séquentiel page 1..N pour reconstruire les résultats accumulés.
  // Cap MAX_HYDRATION_PAGES pour éviter les abus.
  // Retourne { capped: true } si l'URL demandait plus de pages que le cap.
  async function hydrateFromQuery(targetPage: number): Promise<{ capped: boolean }> {
    if (_skipNextSearch) {
      _skipNextSearch = false
      return { capped: false }
    }

    const safePage = Math.min(Math.max(1, targetPage), MAX_HYDRATION_PAGES)
    const capped = targetPage > MAX_HYDRATION_PAGES

    loading.value = true
    error.value = null
    expandedRadiusKm.value = undefined

    try {
      const accumulated: EstablishmentSearchResult[] = []
      let lastResponse: SearchEstablishmentsResponse | null = null

      for (let p = 1; p <= safePage; p++) {
        const response = await rawFetch<SearchEstablishmentsResponse>('/api/search/establishments', {
          query: { ...queryParams.value, page: p },
        })
        accumulated.push(...response.data)
        lastResponse = response
        if (!response.meta.hasMore && p < safePage) break
      }

      if (lastResponse) {
        establishments.value = accumulated
        mapPoints.value = lastResponse.mapPoints
        total.value = lastResponse.meta.total
        page.value = lastResponse.meta.page
        limit.value = lastResponse.meta.limit
        hasMore.value = lastResponse.meta.hasMore
        expandedRadiusKm.value = lastResponse.meta.expandedRadiusKm

        if (capped) {
          // URL pointait trop loin → ramener à la 1re page sans refetch
          isLoadMoreInFlight.value = true
          filters.value = { ...filters.value, page: 1 }
          await router.replace({ query: filtersToQuery(filters.value) })
          isLoadMoreInFlight.value = false
        }
      }
      return { capped }
    }
    catch (e) {
      error.value = e as Error
      return { capped: false }
    }
    finally {
      loading.value = false
    }
  }


  // ============================================================
  // Sync viewport ↔ liste (Pattern C, desktop only)
  // Voir spec: _bmad-output/implementation-artifacts/spec-search-map-list-viewport-sync.md
  // ============================================================

  // Mode 'off' : la liste affiche `establishments` paginé brut (comportement legacy).
  // Mode 'synced' : la liste est filtrée sur `syncedBounds` côté client.
  const viewportSyncMode = ref<'off' | 'synced'>('off')
  const syncedBounds = ref<MapBounds | null>(null)
  // Viewport courante (mise à jour à chaque pan/zoom) — sert au calcul de divergence.
  const currentViewport = ref<MapBounds | null>(null)

  /** Liste affichée à gauche : soit `establishments` brut, soit filtré viewport. */
  const displayedEstablishments = computed<EstablishmentSearchResult[]>(() => {
    if (viewportSyncMode.value !== 'synced' || !syncedBounds.value) {
      return establishments.value
    }
    const bbox = syncedBounds.value
    // On filtre `establishments` via leur mapPoint correspondant (les coordonnées
    // ne sont pas portées par EstablishmentSearchResult, on les retrouve via mapPoints).
    const pointsById = new Map(mapPoints.value.map(p => [p.id, p]))
    return establishments.value.filter((e) => {
      const p = pointsById.get(e.id)
      return p ? bboxContainsPoint(bbox, p) : false
    })
  })

  /** Stats viewport synchronisée : combien chargé / combien total dans la zone. */
  const viewportStats = computed(() => {
    if (viewportSyncMode.value !== 'synced' || !syncedBounds.value) {
      return { loaded: 0, total: 0, hasMissing: false }
    }
    const bbox = syncedBounds.value
    const total = mapPoints.value.filter(p => bboxContainsPoint(bbox, p)).length
    const loaded = displayedEstablishments.value.length
    return { loaded, total, hasMissing: loaded < total }
  })

  /**
   * Divergence entre la viewport courante et l'état affiché.
   * Retourne le max de :
   *  - ratio de cards affichées dont le pin est hors viewport courante
   *  - ratio de pins dans la viewport courante absents de la liste affichée
   * Sert à décider d'afficher le bouton "Rechercher dans cette zone".
   */
  const viewportDivergence = computed<number>(() => {
    if (!currentViewport.value) return 0
    const bbox = currentViewport.value
    const displayed = displayedEstablishments.value
    if (displayed.length === 0 && mapPoints.value.length === 0) return 0

    const inViewport = mapPoints.value.filter(p => bboxContainsPoint(bbox, p))
    const displayedIds = new Set(displayed.map(e => e.id))
    const pointsById = new Map(mapPoints.value.map(p => [p.id, p]))

    const missingFromList = inViewport.filter(p => !displayedIds.has(p.id)).length
    const outsideViewport = displayed.filter((e) => {
      const p = pointsById.get(e.id)
      return p ? !bboxContainsPoint(bbox, p) : false
    }).length

    const fromList = displayed.length > 0 ? outsideViewport / displayed.length : 0
    const fromMap = inViewport.length > 0 ? missingFromList / inViewport.length : 0
    return Math.max(fromList, fromMap)
  })

  /** Vrai dès que la divergence dépasse le seuil — pilote l'affichage du bouton "Rechercher ici". */
  const shouldSuggestRefresh = computed(() =>
    viewportDivergence.value >= MAP_CONFIG.VIEWPORT_DIVERGE_THRESHOLD,
  )

  /** Met à jour la viewport courante (appelé par SearchMapV3 sur moveend/zoomend). */
  function updateCurrentViewport(bbox: MapBounds) {
    currentViewport.value = bbox
  }

  /**
   * Synchronise la liste sur la bbox donnée (clic cluster ou bouton "Rechercher ici").
   * Déclenche automatiquement `loadMissingForViewport` si des pins de la viewport
   * ne sont pas encore chargés dans `establishments` (évite l'état "0 cards / N pins").
   */
  function syncToViewport(bbox: MapBounds) {
    syncedBounds.value = bbox
    viewportSyncMode.value = 'synced'
    // Auto-load des fiches manquantes dans la zone (fire-and-forget)
    if (viewportStats.value.hasMissing && hasMore.value) {
      void loadMissingForViewport()
    }
  }

  /**
   * Synchronise la liste sur la viewport courante stockée (utilisée par le bouton
   * "Rechercher dans cette zone" qui ne dispose pas de la bbox au clic — celle-ci
   * a déjà été poussée par les events viewport-change précédents).
   */
  function syncToCurrentViewport() {
    if (currentViewport.value) syncToViewport(currentViewport.value)
  }

  /** Désactive le mode synced — retour à `establishments` paginé brut. */
  function resetViewportSync() {
    viewportSyncMode.value = 'off'
    syncedBounds.value = null
  }

  /**
   * Charge TOUTES les fiches manquantes pour la viewport synced en une seule cascade
   * `Promise.all` (parallélisation des pages restantes). Appelé automatiquement par
   * `syncToViewport` — l'utilisateur n'a jamais à cliquer "Charger les manquants".
   *
   * Cap dur à VIEWPORT_LOADMORE_LIMIT * 5 (= 300 fiches max) pour protéger d'un cas
   * pathologique (très gros total après filtres serveur). Au-delà, il faudrait passer
   * à un endpoint by-ids (option [2] de la spec, reportée).
   */
  async function loadMissingForViewport(): Promise<void> {
    if (viewportSyncMode.value !== 'synced' || !hasMore.value || loading.value) return

    isLoadMoreInFlight.value = true
    loading.value = true
    error.value = null

    try {
      const pageSize = limit.value || 20
      const startPage = page.value + 1
      // Charge toutes les pages restantes (cap dur à 5x VIEWPORT_LOADMORE_LIMIT pour
      // protéger d'un cas pathologique avec gros total après filtres serveur).
      const remaining = total.value - establishments.value.length
      const hardCap = MAP_CONFIG.VIEWPORT_LOADMORE_LIMIT * 5
      const pagesNeeded = Math.min(
        Math.ceil(hardCap / pageSize),
        Math.ceil(remaining / pageSize),
      )
      if (pagesNeeded <= 0) return

      const responses = await Promise.all(
        Array.from({ length: pagesNeeded }, (_, i) =>
          rawFetch<SearchEstablishmentsResponse>('/api/search/establishments', {
            query: { ...queryParams.value, page: startPage + i, limit: pageSize },
          }),
        ),
      )

      const previousCount = establishments.value.length
      // Merge toutes les pages dans l'ordre, dédupliqué par id
      const existingIds = new Set(establishments.value.map(e => e.id))
      const newEstabs: EstablishmentSearchResult[] = []
      for (const resp of responses) {
        for (const est of resp.data) {
          if (!existingIds.has(est.id)) {
            newEstabs.push(est)
            existingIds.add(est.id)
          }
        }
      }
      establishments.value = [...establishments.value, ...newEstabs]

      const last = responses[responses.length - 1]!
      total.value = last.meta.total
      page.value = last.meta.page
      limit.value = last.meta.limit
      hasMore.value = last.meta.hasMore
      expandedRadiusKm.value = last.meta.expandedRadiusKm

      filters.value = { ...filters.value, page: last.meta.page }
      await router.replace({ query: filtersToQuery(filters.value) })
      if (newEstabs.length > 0) notifyNewResults(newEstabs.length, previousCount)
    }
    catch (e) {
      error.value = e as Error
    }
    finally {
      loading.value = false
      isLoadMoreInFlight.value = false
    }
  }

  // Auto-reset de la sync quand les filtres serveur majeurs changent (region/department/centerLat/q/city).
  // L'utilisateur a relancé une recherche : la viewport synced n'a plus de sens.
  // flush: 'sync' pour reset immédiat avant qu'un re-render n'utilise un état incohérent.
  watch(
    () => [
      filters.value.region,
      filters.value.department,
      filters.value.city,
      filters.value.q,
      filters.value.centerLat,
      filters.value.centerLng,
    ].join('|'),
    (newKey, oldKey) => {
      if (newKey !== oldKey && viewportSyncMode.value === 'synced') {
        resetViewportSync()
      }
    },
    { flush: 'sync' },
  )

  // Compter les filtres actifs
  const activeFilterCount = computed(() => {
    let count = 0
    if (filters.value.q) count++
    if (filters.value.city) count++
    if (filters.value.region) count++
    if (filters.value.checkIn && filters.value.checkOut) count++
    if (filters.value.department) count++
    if (filters.value.minAdults) count++
    if (filters.value.minChildren) count++
    if (filters.value.minPrice !== undefined || filters.value.maxPrice !== undefined) count++
    if (filters.value.radiusKm && filters.value.radiusKm !== SEARCH_FILTERS.DEFAULT_RADIUS_KM) count++
    if (filters.value.hasTableDhotes) count++
    if (filters.value.petsAllowed) count++
    if (filters.value.hasParking) count++
    if (filters.value.hasPool) count++
    if (filters.value.hasWifi) count++
    if (filters.value.hasBikeRental) count++
    if (filters.value.hasSauna) count++
    if (filters.value.hasEVCharger) count++
    if (filters.value.hasHorseFacilities) count++
    // Un critère par séjour type (une étiquette retirable chacun dans « Vos critères »).
    count += filters.value.sejours?.length ?? 0
    // Cadre de vie : un critère par valeur cochée (une étiquette chacune).
    count += filters.value.livingLocality?.length ?? 0
    count += filters.value.livingSurroundings?.length ?? 0
    return count
  })

  return {
    // État
    filters: readonly(filters),
    establishments: readonly(establishments),
    mapPoints: readonly(mapPoints),
    total: readonly(total),
    page: readonly(page),
    limit: readonly(limit),
    hasMore: readonly(hasMore),
    loading: readonly(loading),
    error: readonly(error),
    expandedRadiusKm: readonly(expandedRadiusKm),
    activeFilterCount,
    isLoadMoreInFlight: readonly(isLoadMoreInFlight),
    newResultsAdded: readonly(newResultsAdded),

    // Sync viewport ↔ liste (Pattern C, desktop only)
    viewportSyncMode: readonly(viewportSyncMode),
    displayedEstablishments,
    viewportStats,
    shouldSuggestRefresh,

    // Actions
    updateFilters,
    updateFilter,
    resetFilters,
    search,
    loadMore,
    hydrateFromQuery,
    syncToViewport,
    syncToCurrentViewport,
    resetViewportSync,
    loadMissingForViewport,
    updateCurrentViewport,
  }
}
